import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  TextInput,
  Switch,
  ActivityIndicator,
  KeyboardAvoidingView,
  Keyboard,
  Platform,
  Pressable,
  Alert,
  useColorScheme,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useHeaderHeight } from '@react-navigation/elements';
import { useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BadgeChips } from '../../components/BadgeChips';
import { ModalBottomSheet } from '../../components/ModalBottomSheet';
import { OverflowMenu, type OverflowMenuItem } from '../../components/OverflowMenu';
import { ReportContentModal } from '../../components/ReportContentModal';
import { ForumPostDetailSkeleton } from '../../components/skeletons/ScreenSkeletons';
import { useAuth } from '../../contexts/AuthContext';
import { useMiniProfiles, type MiniProfile } from '../../hooks/useMiniProfiles';
import type { MainStackParamList } from '../../navigation/types';
import { syncBilgeAnnedasBadge } from '../../services/badgeSyncService';
import {
  addComment,
  toggleCommentLike,
  toggleLike,
  updateForumPost,
  subscribeComments,
  subscribeForumPost,
} from '../../services/forumService';
import { beautifyForumText } from '../../services/geminiService';
import { submitReport, type SubmitReportInput } from '../../services/reportService';
import { blockUser } from '../../services/userService';
import { showGuestRegisterPrompt } from '../../utils/guestReadOnlyAlert';
import { hideForumPostLocallyForReporter } from '../../utils/forumReportLocalHide';
import { impactLight } from '../../utils/haptics';
import type { ForumCommentDoc, ForumPostDoc } from '../../types/forum';

export type ForumPostDetailScreenProps = NativeStackScreenProps<
  MainStackParamList,
  'ForumPostDetail'
>;

function formatDate(ts: ForumPostDoc['createdAt'] | ForumCommentDoc['createdAt']): string {
  try {
    const d = ts?.toDate?.() ?? new Date();
    return d.toLocaleString('tr-TR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function ForumPostDetailScreen({ route, navigation }: ForumPostDetailScreenProps) {
  const { postId } = route.params;
  const insets = useSafeAreaInsets();
  const colorScheme = useColorScheme();
  const headerHeight = useHeaderHeight();
  const listRef = useRef<FlatList<ForumCommentDoc>>(null);
  const { user, logout, refreshProfile } = useAuth();
  const isGuest = Boolean(user?.isAnonymous);

  const [post, setPost] = useState<ForumPostDoc | null | undefined>(undefined);
  const [comments, setComments] = useState<ForumCommentDoc[]>([]);
  const [draft, setDraft] = useState('');
  const [replyAnonymous, setReplyAnonymous] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [beautifyCommentBusy, setBeautifyCommentBusy] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [reportBase, setReportBase] = useState<
    Omit<SubmitReportInput, 'reporterId' | 'reasonKey' | 'description'> | null
  >(null);
  /** Şikayet sonrası abonelik gönderiyi geri getirmesin (yalnızca şikayet eden ekranı). */
  const hidePostForReporterRef = useRef(false);
  const hiddenCommentIdsForReporterRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const u1 = subscribeForumPost(
      postId,
      p => {
        if (hidePostForReporterRef.current) {
          return;
        }
        setPost(p);
      },
      () => {
        if (!hidePostForReporterRef.current) {
          setPost(null);
        }
      },
    );
    const u2 = subscribeComments(
      postId,
      list => {
        const filtered = list.filter(c => !hiddenCommentIdsForReporterRef.current.has(c.id));
        setComments(filtered);
      },
      () => {
        if (!hidePostForReporterRef.current) {
          setComments([]);
        }
      },
    );
    return () => {
      u1();
      u2();
    };
  }, [postId]);

  const confirmBlockUser = useCallback(
    (targetUid: string) => {
      if (!user?.uid || targetUid === user.uid) {
        return;
      }
      Alert.alert(
        'Kullanıcıyı engelle',
        'Bu kullanıcının forum gönderileri, yorumları ve ilanları sende görünmez.',
        [
          { text: 'Vazgeç', style: 'cancel' },
          {
            text: 'Engelle',
            style: 'destructive',
            onPress: () => {
              void (async () => {
                try {
                  await blockUser(user.uid, targetUid);
                  await refreshProfile();
                  setComments(prev => prev.filter(c => c.authorId !== targetUid));
                  const isPostAuthor = Boolean(post && post.authorId === targetUid);
                  if (isPostAuthor) {
                    Alert.alert('Engellendi', 'Bu gönderiden çıkılıyor.', [
                      { text: 'Tamam', onPress: () => navigation.goBack() },
                    ]);
                  } else {
                    Alert.alert('Engellendi', 'Bu kullanıcının yorumları gizlendi.');
                  }
                } catch (e) {
                  Alert.alert('Hata', e instanceof Error ? e.message : 'Engellenemedi.');
                }
              })();
            },
          },
        ],
      );
    },
    [user?.uid, post, navigation, refreshProfile],
  );

  useFocusEffect(
    useCallback(() => {
      if (user?.uid && !user.isAnonymous) {
        void syncBilgeAnnedasBadge(user.uid);
      }
    }, [user?.uid, user?.isAnonymous]),
  );

  useEffect(() => {
    const event = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const sub = Keyboard.addListener(event, () => {
      requestAnimationFrame(() => listRef.current?.scrollToEnd({ animated: true }));
    });
    return () => sub.remove();
  }, []);

  const authorIds = useMemo(() => {
    const ids: string[] = [];
    if (post && !post.isAnonymous) {
      ids.push(post.authorId);
    }
    comments.forEach(c => {
      if (!c.isAnonymous) {
        ids.push(c.authorId);
      }
    });
    return ids;
  }, [post, comments]);
  const miniProfiles = useMiniProfiles(authorIds);

  useLayoutEffect(() => {
    if (!post) {
      navigation.setOptions({ headerRight: undefined });
      return;
    }
    const isOwner = post.authorId === user?.uid;
    if (isOwner) {
      navigation.setOptions({
        headerRight: () => (
          <TouchableOpacity
            onPress={() => {
              setEditTitle(post.title);
              setEditContent(post.content);
              setEditError(null);
              setEditOpen(true);
            }}
            className="mr-2 rounded-xl px-2 py-1 active:opacity-80"
            accessibilityRole="button"
            accessibilityLabel="Düzenle">
            <Text className="text-base font-semibold text-zinc-700 dark:text-zinc-400">Düzenle</Text>
          </TouchableOpacity>
        ),
      });
      return;
    }
    if (!isGuest && user?.uid) {
      navigation.setOptions({
        headerRight: () => (
          <View className="mr-1">
            <OverflowMenu
              accessibilityLabel="Gönderi seçenekleri"
              items={[
                {
                  key: 'report-post',
                  label: 'Gönderiyi şikayet et',
                  destructive: true,
                  onPress: () =>
                    setReportBase({
                      targetType: 'forum_post',
                      targetAuthorId: post.authorId,
                      forumPostId: postId,
                    }),
                },
                {
                  key: 'block-post-author',
                  label: 'Kullanıcıyı engelle',
                  destructive: true,
                  onPress: () => confirmBlockUser(post.authorId),
                },
              ]}
            />
          </View>
        ),
      });
      return;
    }
    navigation.setOptions({ headerRight: undefined });
  }, [navigation, post, postId, user?.uid, isGuest, confirmBlockUser]);

  const onLike = useCallback(async () => {
    if (isGuest) {
      showGuestRegisterPrompt(logout);
      return;
    }
    if (!user?.uid || !post) {
      return;
    }
    void impactLight();
    try {
      await toggleLike(postId, user.uid);
    } catch {
      /* sessiz */
    }
  }, [isGuest, logout, postId, user?.uid, post]);

  const onToggleCommentLike = useCallback(
    async (commentId: string) => {
      if (isGuest) {
        showGuestRegisterPrompt(logout);
        return;
      }
      if (!user?.uid) {
        return;
      }
      void impactLight();
      try {
        await toggleCommentLike(postId, commentId, user.uid);
      } catch {
        /* sessiz */
      }
    },
    [isGuest, logout, postId, user?.uid],
  );

  const onSaveEdit = useCallback(async () => {
    if (!user?.uid) {
      return;
    }
    const t = editTitle.trim();
    const c = editContent.trim();
    if (t.length < 3 || c.length < 10) {
      setEditError('Başlık ve içerik yeterince uzun olmalı.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await updateForumPost(postId, user.uid, { title: t, content: c });
      setEditOpen(false);
    } catch (e: unknown) {
      setEditError(e instanceof Error ? e.message : 'Kaydedilemedi.');
    } finally {
      setEditSaving(false);
    }
  }, [user?.uid, postId, editTitle, editContent]);

  const canBeautifyComment = draft.trim().length >= 4;

  const onBeautifyComment = useCallback(async () => {
    if (!canBeautifyComment || beautifyCommentBusy || sending) {
      return;
    }
    void impactLight();
    setBeautifyCommentBusy(true);
    setSendError(null);
    try {
      const next = await beautifyForumText(draft, 'comment');
      setDraft(next);
    } catch (e) {
      Alert.alert(
        'Güzelleştirilemedi',
        e instanceof Error ? e.message : 'Bağlantıyı kontrol edip tekrar dene.',
      );
    } finally {
      setBeautifyCommentBusy(false);
    }
  }, [draft, canBeautifyComment, beautifyCommentBusy, sending]);

  const onSendComment = useCallback(async () => {
    if (isGuest) {
      showGuestRegisterPrompt(logout);
      return;
    }
    if (!user?.uid) {
      setSendError('Oturum gerekli.');
      return;
    }
    const t = draft.trim();
    if (t.length < 2) {
      setSendError('Yorum en az 2 karakter olsun.');
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      void impactLight();
      await addComment(postId, user.uid, t, replyAnonymous);
      setDraft('');
    } catch {
      setSendError('Yorum gönderilemedi.');
    } finally {
      setSending(false);
    }
  }, [isGuest, logout, user?.uid, draft, replyAnonymous, postId]);

  if (post === undefined) {
    return (
 <View className="flex-1 bg-white dark:bg-black">
        <ForumPostDetailSkeleton />
      </View>
    );
  }

  if (post === null) {
    return (
 <View className="flex-1 items-center justify-center bg-white dark:bg-black px-6 dark:bg-black">
 <Text className="text-center text-gray-600 dark:text-zinc-400">
          Bu gönderi bulunamadı veya silindi.
        </Text>
      </View>
    );
  }

  const liked = Boolean(user?.uid && post.likedBy.includes(user.uid));
  const likeCount = post.likedBy.length;
  const postAuthorMini: MiniProfile | undefined = post.isAnonymous
    ? undefined
    : miniProfiles[post.authorId];
  const postAuthorLabel = post.isAnonymous ? 'Bir Annedaş' : postAuthorMini?.displayName ?? 'Annedaş';

  return (
    <>
    <KeyboardAvoidingView
      className="flex-1 bg-white dark:bg-black"
      style={{ flex: 1 }}
      behavior="padding"
      keyboardVerticalOffset={headerHeight}>
      <FlatList
        ref={listRef}
        data={comments}
        keyExtractor={c => c.id}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: 12,
          paddingBottom: 24 + insets.bottom + 120,
        }}
        ListHeaderComponent={
 <View className="mb-6">
 <View className="mb-3 flex-row items-center justify-between">
              <View
 className="rounded-full px-2.5 py-1"
                style={{ backgroundColor: '#fff7ed', borderWidth: 1, borderColor: '#fed7aa' }}>
 <Text className="text-xs font-semibold text-zinc-800 dark:text-zinc-300">
                  {post.category}
                </Text>
              </View>
 <Text className="text-xs text-gray-400 dark:text-zinc-500">{formatDate(post.createdAt)}</Text>
            </View>

 <View className="mb-3 flex-row flex-wrap items-center gap-x-1">
 <Text className="text-2xl font-bold text-gray-800 dark:text-zinc-100">{post.title}</Text>
            </View>
 <View className="mb-2 flex-row flex-wrap items-center gap-x-1">
 <Text className="text-xs font-medium text-gray-500 dark:text-zinc-400">
                {post.isAnonymous ? 'Anonim' : postAuthorLabel}
              </Text>
              {!post.isAnonymous ? <BadgeChips badges={postAuthorMini?.badges} compact /> : null}
            </View>
 <Text className="mb-5 text-base leading-6 text-gray-700 dark:text-zinc-300">{post.content}</Text>

 <View className="flex-row flex-wrap items-center gap-4">
              <TouchableOpacity
                onPress={() => void onLike()}
                activeOpacity={0.85}
                className="flex-row items-center rounded-2xl border border-zinc-300 bg-white px-4 py-2.5 dark:border-zinc-800 dark:bg-zinc-900">
                <Text className="text-base">{liked ? '🌹' : '🤍'}</Text>
                <Text
                  className={`ml-2 text-sm font-semibold ${
                    liked ? 'text-zinc-700 dark:text-zinc-300' : 'text-zinc-600 dark:text-zinc-400'
                  }`}>
                  {liked ? 'Beğendin' : 'Beğen'}
                </Text>
                <Text className="ml-2 text-sm text-zinc-500 dark:text-zinc-400">({likeCount})</Text>
              </TouchableOpacity>
 <View className="flex-row items-center rounded-2xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-900/90 px-4 py-2.5 dark:border-zinc-800 dark:bg-zinc-900/50">
 <Text className="text-base">💬</Text>
 <Text className="ml-2 text-sm font-semibold text-zinc-800 dark:text-zinc-300">
                  {post.commentCount} yorum
                </Text>
              </View>
            </View>

 <Text className="mb-2 mt-8 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
              Yorumlar
            </Text>
          </View>
        }
        ListEmptyComponent={
 <Text className="py-4 text-center text-sm text-gray-500 dark:text-zinc-400">
            Henüz yorum yok.
          </Text>
        }
        renderItem={({ item }) => (
          <CommentBubble
            comment={item}
            authorMini={item.isAnonymous ? undefined : miniProfiles[item.authorId]}
            currentUserId={user?.uid}
            isGuest={isGuest}
            onToggleLike={() => void onToggleCommentLike(item.id)}
            overflowMenuItems={
              !isGuest && user?.uid && item.authorId !== user.uid
                ? [
                    {
                      key: 'report-comment',
                      label: 'Yorumu şikayet et',
                      destructive: true,
                      onPress: () =>
                        setReportBase({
                          targetType: 'forum_comment',
                          targetAuthorId: item.authorId,
                          forumPostId: postId,
                          forumCommentId: item.id,
                        }),
                    },
                    {
                      key: 'block-comment-author',
                      label: 'Kullanıcıyı engelle',
                      destructive: true,
                      onPress: () => confirmBlockUser(item.authorId),
                    },
                  ]
                : undefined
            }
          />
        )}
      />

      <View
 className="border-t border-zinc-200 bg-white dark:bg-black px-4 pt-3 dark:border-zinc-800 dark:bg-black"
        style={{ paddingBottom: Math.max(insets.bottom, 12) }}>
 <View className="mb-2 flex-row items-center justify-between rounded-xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-900 px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900/40">
 <Text className="flex-1 text-sm text-gray-700 dark:text-zinc-300">Anonim yanıtla</Text>
          <Switch
            value={replyAnonymous}
            onValueChange={setReplyAnonymous}
            trackColor={{ false: '#e4e4e7', true: '#a1a1aa' }}
            thumbColor={replyAnonymous ? '#fafafa' : '#f4f4f5'}
          />
        </View>
        <TextInput
          value={draft}
          onChangeText={t => {
            setDraft(t);
            setSendError(null);
          }}
          placeholder="Yorumunu yaz…"
          placeholderTextColor="#9ca3af"
          multiline
          editable={!beautifyCommentBusy}
          className="mb-2 min-h-[44px] rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-base text-gray-800 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100"
          textAlignVertical="top"
        />
        <TouchableOpacity
          activeOpacity={0.88}
          disabled={!canBeautifyComment || beautifyCommentBusy || sending}
          onPress={() => void onBeautifyComment()}
          className={`mb-2 rounded-xl border px-3 py-2.5 ${
            !canBeautifyComment || sending
              ? 'border-zinc-200 bg-zinc-50 opacity-60 dark:border-zinc-800 dark:bg-zinc-900/60'
              : 'border-zinc-300 bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800'
          }`}>
          {beautifyCommentBusy ? (
            <ActivityIndicator size="small" color={colorScheme === 'dark' ? '#fafafa' : '#18181b'} />
          ) : (
            <Text className="text-center text-sm font-semibold text-zinc-800 dark:text-zinc-200">
              Metni güzelleştir
            </Text>
          )}
        </TouchableOpacity>
        {sendError ? (
 <Text className="mb-2 text-center text-sm text-zinc-700">{sendError}</Text>
        ) : null}
        <TouchableOpacity
          onPress={onSendComment}
          disabled={sending}
          className="rounded-2xl border border-zinc-900 bg-zinc-900 py-3.5 dark:border-zinc-50 dark:bg-zinc-50">
          {sending ? (
            <ActivityIndicator color={colorScheme === 'dark' ? '#18181b' : '#fff'} />
          ) : (
            <Text className="text-center font-semibold text-white dark:text-zinc-950">Gönder</Text>
          )}
        </TouchableOpacity>
      </View>

      <ModalBottomSheet
        visible={editOpen}
        onRequestClose={() => !editSaving && setEditOpen(false)}
        dismissDisabled={editSaving}
        keyboardAvoiding>
        <View
          className="rounded-t-3xl border border-zinc-200 bg-white px-5 pt-4 dark:border-zinc-800 dark:bg-zinc-900"
          style={{ paddingBottom: 24 + insets.bottom }}>
 <View className="mb-3 h-1 w-10 self-center rounded-full bg-zinc-200 dark:bg-zinc-700 dark:bg-zinc-700" />
 <Text className="mb-3 text-lg font-bold text-gray-900 dark:text-zinc-100">
              Gönderiyi düzenle
            </Text>
 <Text className="mb-1 text-xs font-medium text-gray-500">Başlık</Text>
            <TextInput
              value={editTitle}
              onChangeText={setEditTitle}
 className="mb-3 rounded-2xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-900/80 px-4 py-3 text-base text-gray-900 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100"
            />
 <Text className="mb-1 text-xs font-medium text-gray-500">İçerik</Text>
            <TextInput
              value={editContent}
              onChangeText={setEditContent}
              multiline
              textAlignVertical="top"
 className="mb-3 min-h-[140px] rounded-2xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-900/80 px-4 py-3 text-base text-gray-900 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100"
            />
            {editError ? (
 <Text className="mb-3 text-sm text-zinc-700">{editError}</Text>
            ) : null}
 <View className="flex-row gap-3">
              <TouchableOpacity
                disabled={editSaving}
                onPress={() => setEditOpen(false)}
 className="flex-1 rounded-2xl border border-zinc-300 py-3.5 dark:border-zinc-800">
 <Text className="text-center font-semibold text-gray-600 dark:text-zinc-300">Vazgeç</Text>
              </TouchableOpacity>
              <TouchableOpacity
                disabled={editSaving}
                onPress={() => void onSaveEdit()}
                className="flex-1 rounded-2xl border border-zinc-900 bg-zinc-900 py-3.5 dark:border-zinc-50 dark:bg-zinc-50">
                {editSaving ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text className="text-center font-semibold text-white dark:text-zinc-950">Kaydet</Text>
                )}
              </TouchableOpacity>
            </View>
        </View>
      </ModalBottomSheet>

    </KeyboardAvoidingView>

    {reportBase && user?.uid ? (
      <ReportContentModal
        visible
        title={
          reportBase.targetType === 'forum_post' ? 'Gönderiyi şikayet et' : 'Yorumu şikayet et'
        }
        subtitle="Sebebi seçip kısaca açıklayın. Kişisel veri paylaşmayın."
        reportBase={reportBase}
        onClose={() => setReportBase(null)}
        onSubmit={async partial => {
          await submitReport({ ...partial, reporterId: user.uid });
          if (partial.targetType === 'forum_post') {
            hidePostForReporterRef.current = true;
            hideForumPostLocallyForReporter(postId);
            Alert.alert(
              'Teşekkürler',
              'Şikayetin kaydedildi. Gönderi listenizden kaldırıldı; incelenecek.',
              [{ text: 'Tamam', onPress: () => navigation.goBack() }],
              { cancelable: false },
            );
          } else if (partial.targetType === 'forum_comment' && partial.forumCommentId) {
            hiddenCommentIdsForReporterRef.current.add(partial.forumCommentId);
            setComments(prev => prev.filter(c => c.id !== partial.forumCommentId));
            Alert.alert(
              'Teşekkürler',
              'Şikayetin kaydedildi. Yorum senin ekranından kaldırıldı; incelenecek.',
            );
          }
        }}
      />
    ) : null}
    </>
  );
}

function CommentBubble({
  comment,
  authorMini,
  currentUserId,
  isGuest,
  onToggleLike,
  overflowMenuItems,
}: {
  comment: ForumCommentDoc;
  authorMini?: MiniProfile;
  currentUserId?: string;
  isGuest: boolean;
  onToggleLike: () => void;
  overflowMenuItems?: OverflowMenuItem[];
}) {
  const liked = Boolean(currentUserId && comment.likedBy.includes(currentUserId));
  const likeCount = comment.likedBy.length;
  const label = comment.isAnonymous ? 'Bir Annedaş' : authorMini?.displayName ?? 'Annedaş';

  return (
    <View className="mb-4 rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
 <View className="mb-2 flex-row items-start justify-between gap-2 pr-1">
        {comment.isAnonymous ? (
 <View className="min-w-0 flex-1 flex-row items-center">
            <View
 className="mr-2 h-8 w-8 items-center justify-center rounded-full border border-zinc-200 dark:border-zinc-800"
              style={{ backgroundColor: '#fff1f2' }}>
              <Text style={{ fontSize: 16 }}>🌸</Text>
            </View>
 <Text className="text-sm font-semibold text-gray-700 dark:text-zinc-200">Bir Annedaş</Text>
          </View>
        ) : (
 <View className="min-w-0 flex-1 flex-row items-center">
            <View
 className="mr-2 h-8 w-8 items-center justify-center rounded-full border border-orange-50 dark:border-zinc-800"
              style={{ backgroundColor: '#fff7ed' }}>
              <Text style={{ fontSize: 14 }}>👤</Text>
            </View>
 <View className="min-w-0 flex-1">
 <View className="flex-row flex-wrap items-center gap-x-0.5">
 <Text className="text-sm font-semibold text-gray-700 dark:text-zinc-200" numberOfLines={1}>
                  {label}
                </Text>
                <BadgeChips badges={authorMini?.badges} compact />
              </View>
 <Text className="text-xs text-gray-400 dark:text-zinc-500">Üye</Text>
            </View>
          </View>
        )}
 <View className="flex-row items-center gap-1">
          <Text className="text-xs text-gray-400 dark:text-zinc-500">
            {formatDate(comment.createdAt)}
          </Text>
          {overflowMenuItems && overflowMenuItems.length > 0 ? (
            <OverflowMenu size="sm" items={overflowMenuItems} accessibilityLabel="Yorum seçenekleri" />
          ) : null}
        </View>
      </View>
 <Text className="text-sm leading-5 text-gray-700 dark:text-zinc-300">{comment.content}</Text>
 <View className="mt-2 flex-row flex-wrap items-center justify-end gap-2 border-t border-zinc-200 pt-2 dark:border-zinc-800">
        <TouchableOpacity
          onPress={onToggleLike}
          disabled={!currentUserId}
          activeOpacity={0.8}
 className="flex-row items-center rounded-xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-950 px-2.5 py-1 dark:border-zinc-800 dark:bg-zinc-900">
 <Text className="text-sm" style={{ opacity: currentUserId ? 1 : 0.45 }}>
            {liked ? '🌹' : '🤍'}
          </Text>
          <Text
            className={`ml-1 text-xs font-semibold ${
              liked ? 'text-zinc-700 dark:text-zinc-300' : 'text-zinc-500 dark:text-zinc-400'
            }`}>
            {likeCount}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}
